import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { useInstalledAppVersionsQuery } from "./hooks/useInstalledAppVersionsQuery";
import { AppVersionItem, InstalledAppVersionsData } from "./types";

interface InstalledAppVersionsProps {
  initialData?: InstalledAppVersionsData;
  onRefresh?: () => void;
}

export const InstalledAppVersions: React.FC<InstalledAppVersionsProps> = ({ initialData, onRefresh }) => {
  const { data: fetchedData, refetch } = useInstalledAppVersionsQuery();

  const data = initialData || fetchedData || {
    total: 0,
    page: 1,
    pageSize: 50,
    totalPages: 1,
    counts: { workforces: 0, users: 0, outdated: 0, unsupported: 0 },
    summaryByVersion: [],
    items: [],
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [targetTypeFilter, setTargetTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredItems = useMemo(() => {
    return data.items.filter((item: AppVersionItem) => {
      // Filter by search term
      const matchesSearch =
        !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.appVersion.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by platform
      const matchesPlatform = platformFilter === "all" || item.appPlatform.toLowerCase() === platformFilter;

      // Filter by target type
      const matchesTargetType = targetTypeFilter === "all" || item.targetType.toLowerCase() === targetTypeFilter;

      // Filter by status
      let matchesStatus = true;
      if (statusFilter === "outdated") matchesStatus = !item.isUpToDate;
      else if (statusFilter === "unsupported") matchesStatus = !item.isSupported;
      else if (statusFilter === "upToDate") matchesStatus = item.isUpToDate;

      return matchesSearch && matchesPlatform && matchesTargetType && matchesStatus;
    });
  }, [data.items, searchTerm, platformFilter, targetTypeFilter, statusFilter]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(d);
    } catch {
      return dateStr;
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 }, bgcolor: "var(--fx-surface-sunken, #F8FAFC)", minHeight: "100%" }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: "var(--fx-colors-info-50, #EFF6FF)", color: "#0284C7", width: 44, height: 44 }}>
            <SmartphoneIcon fontSize="medium" />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700} color="var(--fx-surface-text, #0F172A)">
              Devices & App Versions
            </Typography>
            <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)">
              Monitor mobile app versions in use across workforce personnel and users
            </Typography>
          </Box>
        </Box>

        {onRefresh && (
          <Tooltip title="Refresh Data">
            <IconButton onClick={onRefresh} sx={{ bgcolor: "var(--fx-surface-base, #FFFFFF)", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Metric KPI Cards */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              background: "linear-gradient(135deg, var(--fx-surface-base, #FFFFFF) 0%, var(--fx-surface-muted, #F1F5F9) 100%)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)" textTransform="uppercase">
              Total Devices
            </Typography>
            <Typography variant="h4" fontWeight={800} color="var(--fx-surface-text, #0F172A)" mt={0.5}>
              {data.total}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <SmartphoneIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                Registered
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)" textTransform="uppercase">
              Workforces
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#2563EB" mt={0.5}>
              {data.counts.workforces}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <EngineeringIcon sx={{ fontSize: 16, color: "#2563EB" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                Field personnel
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)" textTransform="uppercase">
              Users
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#0D9488" mt={0.5}>
              {data.counts.users}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <PersonIcon sx={{ fontSize: 16, color: "#0D9488" }} />
              <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                App users
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid #FED7AA",
              bgcolor: "var(--fx-colors-yellow-50, #FFFDEB)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#B45309" textTransform="uppercase">
              Outdated
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#D97706" mt={0.5}>
              {data.counts.outdated}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <WarningAmberIcon sx={{ fontSize: 16, color: "#D97706" }} />
              <Typography variant="caption" color="#B45309">
                Requires update
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid #FECDD3",
              bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#9F1239" textTransform="uppercase">
              Unsupported
            </Typography>
            <Typography variant="h4" fontWeight={800} color="#E11D48" mt={0.5}>
              {data.counts.unsupported}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={1}>
              <ErrorOutlineIcon sx={{ fontSize: 16, color: "#E11D48" }} />
              <Typography variant="caption" color="#9F1239">
                Critical / Obsolete
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Summary by Version & Filters Bar */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "16px", mb: 3, border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
        <Typography variant="subtitle2" fontWeight={700} color="var(--fx-surface-textsecondary, #64748B)" mb={1.5}>
          Summary by Installed Version
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2.5}>
          {data.summaryByVersion.map((v) => (
            <Chip
              key={v.appVersion}
              icon={<SystemUpdateIcon style={{ fontSize: 16 }} />}
              label={`v${v.appVersion} (${v.count} ${v.count === 1 ? "device" : "devices"})`}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                fontWeight: 600,
                color: "var(--fx-surface-text, #0F172A)",
                borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Filter Controls */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, company, version..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={2.6}>
            <Select
              fullWidth
              size="small"
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              sx={{ borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterListIcon sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: 18 }} />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Platforms</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
              <MenuItem value="android">Android</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12} sm={4} md={2.7}>
            <Select
              fullWidth
              size="small"
              value={targetTypeFilter}
              onChange={(e) => setTargetTypeFilter(e.target.value)}
              sx={{ borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}
            >
              <MenuItem value="all">All Target Types</MenuItem>
              <MenuItem value="workforce">Workforce</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={12} sm={4} md={2.7}>
            <Select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="upToDate">Up to Date</MenuItem>
              <MenuItem value="outdated">Outdated</MenuItem>
              <MenuItem value="unsupported">Unsupported</MenuItem>
            </Select>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: "var(--fx-surface-muted, #F1F5F9)" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>User / Staff</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>Platform</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>App Version</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>Update Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>API Support</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)" }}>Last Activity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)">
                    No devices found matching the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item: AppVersionItem) => (
                <TableRow key={item.uid} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          bgcolor: item.targetType === "workforce" ? "var(--fx-colors-info-100, #DBEAFE)" : "var(--fx-colors-success-100, #D1FAE5)",
                          color: item.targetType === "workforce" ? "#1E40AF" : "#0F766E",
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        {item.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2" fontWeight={600} color="var(--fx-surface-text, #0F172A)">
                            {item.name}
                          </Typography>
                          <Chip
                            label={item.targetType}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              bgcolor: item.targetType === "workforce" ? "var(--fx-colors-info-50, #EFF6FF)" : "var(--fx-surface-sunken, #F8FAFC)",
                              color: item.targetType === "workforce" ? "#2563EB" : "#0D9488",
                              border: `1px solid ${item.targetType === "workforce" ? "#BFDBFE" : "#99F6E4"}`,
                            }}
                          />
                        </Stack>
                        <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                          {item.email ? item.email : `UID: ${item.uid.substring(0, 8)}...`}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <BusinessIcon sx={{ fontSize: 16, color: "var(--fx-surface-textsecondary, #64748B)" }} />
                      <Typography variant="body2" color="var(--fx-surface-textsecondary, #64748B)" fontWeight={500}>
                        {item.companyName}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      icon={
                        item.appPlatform.toLowerCase() === "ios" ? <AppleIcon style={{ fontSize: 16 }} /> : <AndroidIcon style={{ fontSize: 16 }} />
                      }
                      label={item.appPlatform.toUpperCase()}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: "6px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        borderColor: item.appPlatform.toLowerCase() === "ios" ? "var(--fx-surface-texttertiary, #94A3B8)" : "#86EFAC",
                        color: item.appPlatform.toLowerCase() === "ios" ? "var(--fx-surface-text, #0F172A)" : "#166534",
                        bgcolor: item.appPlatform.toLowerCase() === "ios" ? "var(--fx-surface-sunken, #F8FAFC)" : "var(--fx-colors-success-50, #ECFDF5)",
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight={700} color="var(--fx-surface-text, #0F172A)">
                      v{item.appVersion}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {item.isUpToDate ? (
                      <Chip
                        icon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />}
                        label="Up to date"
                        size="small"
                        sx={{
                          bgcolor: "var(--fx-colors-success-100, #D1FAE5)",
                          color: "#15803D",
                          fontWeight: 700,
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<WarningAmberIcon style={{ fontSize: 16 }} />}
                        label="Outdated"
                        size="small"
                        sx={{
                          bgcolor: "var(--fx-colors-yellow-100, #FFF9C2)",
                          color: "#B45309",
                          fontWeight: 700,
                          borderRadius: "6px",
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell>
                    {item.isSupported ? (
                      <Chip
                        label="Supported"
                        size="small"
                        sx={{
                          bgcolor: "var(--fx-colors-info-50, #EFF6FF)",
                          color: "#0369A1",
                          fontWeight: 600,
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<ErrorOutlineIcon style={{ fontSize: 16 }} />}
                        label="Unsupported"
                        size="small"
                        sx={{
                          bgcolor: "var(--fx-colors-danger-50, #FEF2F2)",
                          color: "#BE123C",
                          fontWeight: 700,
                          borderRadius: "6px",
                        }}
                      />
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" color="var(--fx-surface-textsecondary, #64748B)">
                      {formatDate(item.appVersionUpdatedAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InstalledAppVersions;
